import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  LinearProgress,
  Stack,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import {
  Add as AddIcon,
  Apartment as ApartmentIcon,
  CalendarMonth as CalendarMonthIcon,
  Campaign as CampaignIcon,
  CheckCircle as CheckCircleIcon,
  FlightTakeoffRounded as FlightTakeoffRoundedIcon,
  HourglassEmpty as HourglassEmptyIcon,
  LocationSearching as LocationSearchingIcon,
  Login as LoginIcon,
  Logout as LogoutIcon,
  Person as PersonIcon,
  Refresh as RefreshIcon,
  RequestPageRounded as RequestPageRoundedIcon,
  TodayRounded as TodayRoundedIcon,
} from '@mui/icons-material';

import api from '../../services/api';
import {
  daysUntilDate,
  formatDateOnly,
  isAnnouncementActive,
  isHolidayUpcoming,
} from '../../utils/notificationUtils';
import {
  DetailField,
  LoadingState,
  PageLayout,
  StatusBadge,
} from '../../components/ui';

const toMysql = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  const hh = String(date.getHours()).padStart(2, '0');
  const mm = String(date.getMinutes()).padStart(2, '0');
  const ss = String(date.getSeconds()).padStart(2, '0');
  return `${y}-${m}-${d} ${hh}:${mm}:${ss}`;
};

const asArray = (value) => {
  if (Array.isArray(value)) return value;
  if (value && Array.isArray(value.data)) return value.data;
  if (value && Array.isArray(value.items)) return value.items;
  return [];
};

const calculateServicePeriod = (joinDate) => {
  if (!joinDate) return 'N/A';
  const start = new Date(joinDate);
  if (Number.isNaN(start.getTime())) return 'N/A';
  const now = new Date();
  let years = now.getFullYear() - start.getFullYear();
  let months = now.getMonth() - start.getMonth();
  if (now.getDate() < start.getDate()) months -= 1;
  if (months < 0) {
    years -= 1;
    months += 12;
  }
  return `${years}y ${months}m`;
};

const daysRemainingInYear = () => {
  const now = new Date();
  return Math.ceil((new Date(now.getFullYear(), 11, 31) - now) / 86400000);
};

const annualAllowance = (contract) => {
  if (contract === '30 Days Yearly') return 30;
  if (contract === '24 Days Yearly') return 24;
  if (contract === '21 Days Yearly') return 21;
  if (contract === '20 Days Yearly') return 20;
  return null;
};

const getDailyAccrualRate = (contract, type) => {
  if (type !== 'Annual') return 0;
  const allowance = annualAllowance(contract);
  return allowance ? allowance / 365 : 0;
};

const calculateDistance = (lat1, lon1, lat2, lon2) => {
  const R = 6371000;
  const phi1 = (lat1 * Math.PI) / 180;
  const phi2 = (lat2 * Math.PI) / 180;
  const dPhi = ((lat2 - lat1) * Math.PI) / 180;
  const dLambda = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dPhi / 2) ** 2 +
    Math.cos(phi1) * Math.cos(phi2) * Math.sin(dLambda / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

const getNearbyProjectId = (lat, lng, projects) => {
  for (const project of projects || []) {
    const projectLat = Number(project.latitude);
    const projectLng = Number(project.longitude);
    if (!Number.isFinite(projectLat) || !Number.isFinite(projectLng)) continue;
    const distance = calculateDistance(lat, lng, projectLat, projectLng);
    if (distance <= (Number(project.allowed_radius_in_meters) || 300)) return project.id;
  }
  return null;
};

const requestDate = (request) =>
  request?.start_date || request?.created_at || request?.request_date || request?.date || '';

const normalizeStatus = (status) => String(status || 'pending').toLowerCase();

function DashboardPanel({ title, subtitle, action, children, sx }) {
  const theme = useTheme();

  return (
    <Box
      sx={[
        {
          borderRadius: { xs: '14px', md: '16px' },
          border: '1px solid',
          borderColor: alpha('#9db2c8', 0.38),
          background:
            theme.palette.mode === 'dark'
              ? `linear-gradient(180deg, ${alpha('#111827', 0.84)}, ${alpha('#0f172a', 0.78)})`
              : `linear-gradient(180deg, ${alpha('#ffffff', 0.96)}, ${alpha('#f8fbff', 0.92)})`,
          boxShadow: '0 16px 38px rgba(15, 23, 42, 0.08)',
          overflow: 'hidden',
          backdropFilter: 'blur(14px)',
        },
        sx,
      ]}
    >
      {title || action ? (
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          alignItems={{ sm: 'flex-start' }}
          justifyContent="space-between"
          sx={{ px: { xs: 1.45, sm: 1.75 }, pt: { xs: 1.45, sm: 1.75 }, pb: 0.9 }}
        >
          <Box sx={{ minWidth: 0 }}>
            {title ? (
              <Typography variant="subtitle1" sx={{ fontWeight: 900, lineHeight: 1.15, color: '#102033' }}>
                {title}
              </Typography>
            ) : null}
            {subtitle ? (
              <Typography variant="body2" sx={{ mt: 0.35, color: '#64748b' }}>
                {subtitle}
              </Typography>
            ) : null}
          </Box>
          {action}
        </Stack>
      ) : null}
      <Box sx={{ px: { xs: 1.45, sm: 1.75 }, pb: { xs: 1.45, sm: 1.75 }, pt: title || action ? 0 : { xs: 1.45, sm: 1.75 } }}>
        {children}
      </Box>
    </Box>
  );
}

function MiniEmpty({ title, description }) {
  const theme = useTheme();

  return (
    <Box
      sx={{
        p: 1.3,
        borderRadius: '12px',
        border: '1px dashed',
        borderColor: alpha('#9db2c8', 0.42),
        backgroundColor: theme.palette.mode === 'dark' ? alpha('#ffffff', 0.03) : alpha('#f8fbff', 0.9),
      }}
    >
      <Typography variant="subtitle2" sx={{ fontWeight: 850 }}>
        {title}
      </Typography>
      {description ? (
        <Typography variant="caption" color="text.secondary">
          {description}
        </Typography>
      ) : null}
    </Box>
  );
}

function DashboardAction({ icon, title, description, badge, color = 'primary.main', onClick }) {
  const theme = useTheme();
  const [paletteKey, shadeKey] = String(color).split('.');
  const accent = theme.palette?.[paletteKey]?.[shadeKey] || color;

  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      sx={{
        width: '100%',
        minHeight: 88,
        p: 1.35,
        border: '1px solid',
        borderColor: alpha(accent, 0.22),
        borderRadius: '14px',
        background:
          theme.palette.mode === 'dark'
            ? `linear-gradient(145deg, ${alpha(accent, 0.11)}, ${alpha('#ffffff', 0.025)})`
            : `linear-gradient(145deg, ${alpha('#ffffff', 0.98)}, ${alpha(accent, 0.065)})`,
        boxShadow: '0 10px 24px rgba(15, 23, 42, 0.06)',
        cursor: 'pointer',
        color: 'text.primary',
        font: 'inherit',
        textAlign: 'left',
        transition: theme.transitions.create(['background-color', 'border-color', 'box-shadow'], {
          duration: theme.transitions.duration.shortest,
        }),
        '&:hover': {
          borderColor: alpha(accent, 0.38),
          backgroundColor: theme.palette.mode === 'dark' ? alpha(accent, 0.14) : alpha(accent, 0.055),
          boxShadow: '0 16px 32px rgba(15, 23, 42, 0.1)',
          transform: 'translateY(-2px)',
        },
      }}
    >
      <Stack direction="row" spacing={1.15} alignItems="flex-start">
        <Box
          sx={{
            width: 38,
            height: 38,
            borderRadius: '12px',
            display: 'grid',
            placeItems: 'center',
            color: accent,
            backgroundColor: alpha(accent, theme.palette.mode === 'dark' ? 0.18 : 0.1),
            flexShrink: 0,
          }}
        >
          {icon}
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Stack direction="row" spacing={0.75} alignItems="center">
            <Typography variant="subtitle2" sx={{ fontWeight: 850, lineHeight: 1.18 }}>
              {title}
            </Typography>
            {badge ? <Chip size="small" label={badge} sx={{ height: 22, fontWeight: 800 }} /> : null}
          </Stack>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.35, lineHeight: 1.35 }}>
            {description}
          </Typography>
        </Box>
      </Stack>
    </Box>
  );
}

function SummaryStat({ icon, label, value, hint, color = 'primary.main' }) {
  const theme = useTheme();
  const [paletteKey, shadeKey] = String(color).split('.');
  const accent = theme.palette?.[paletteKey]?.[shadeKey] || color;

  return (
    <DashboardPanel
      sx={{
        height: '100%',
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          inset: '0 auto 0 0',
          width: 4,
          background: `linear-gradient(180deg, ${accent}, ${alpha(accent, 0.28)})`,
        },
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center">
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: '14px',
            display: 'grid',
            placeItems: 'center',
            color: accent,
            background: `linear-gradient(145deg, ${alpha(accent, 0.18)}, ${alpha(accent, 0.07)})`,
            border: `1px solid ${alpha(accent, 0.16)}`,
          }}
        >
          {icon}
        </Box>
        <Box sx={{ minWidth: { sm: 118, md: 128 }, flex: 1 }}>
          <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 900, whiteSpace: 'nowrap' }}>
            {label}
          </Typography>
          <Typography variant="h5" sx={{ mt: 0.15, lineHeight: 1, fontWeight: 950, color: '#102033' }}>
            {value}
          </Typography>
          {hint ? (
            <Typography variant="caption" sx={{ color: '#64748b' }}>
              {hint}
            </Typography>
          ) : null}
        </Box>
        <Box
          aria-hidden
          sx={{
            width: 74,
            height: 38,
            display: { xs: 'none', sm: 'block' },
            flexShrink: 0,
            alignSelf: 'flex-end',
          }}
        >
          <svg viewBox="0 0 92 42" width="100%" height="100%" preserveAspectRatio="none">
            <path
              d="M2 32 C15 29, 18 23, 29 26 C39 29, 42 18, 53 21 C65 25, 70 11, 80 13 C86 14, 88 7, 90 5"
              fill="none"
              stroke={accent}
              strokeWidth="3"
              strokeLinecap="round"
            />
            <path
              d="M2 38 C15 35, 18 29, 29 32 C39 35, 42 24, 53 27 C65 31, 70 17, 80 19 C86 20, 88 13, 90 11 L90 42 L2 42 Z"
              fill={alpha(accent, 0.1)}
            />
          </svg>
        </Box>
      </Stack>
    </DashboardPanel>
  );
}

function LeaveBalanceRow({ balance, contractType }) {
  const theme = useTheme();
  const current = Number(balance.current_balance || 0);
  const allowance = balance.leave_type === 'Annual' ? annualAllowance(contractType) : null;
  const max = allowance || Math.max(current, Number(balance.projected_balance || 0), 1);
  const pct = Math.max(0, Math.min(100, (current / max) * 100));

  return (
    <Box
      sx={{
        p: 1,
        borderRadius: '12px',
        border: '1px solid',
        borderColor: alpha('#9db2c8', 0.38),
        backgroundColor: theme.palette.mode === 'dark' ? alpha('#ffffff', 0.035) : alpha('#ffffff', 0.88),
        boxShadow: '0 8px 18px rgba(15, 23, 42, 0.04)',
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 850 }}>
            {balance.leave_type || 'Leave'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Projected {Number(balance.projected_balance || current).toFixed(1)} days
          </Typography>
        </Box>
        <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>
          {current.toFixed(1)}
        </Typography>
      </Stack>
      <LinearProgress
        variant="determinate"
        value={pct}
        sx={{
          mt: 0.9,
          height: 7,
          borderRadius: 999,
          backgroundColor: alpha(theme.palette.primary.main, 0.09),
          '& .MuiLinearProgress-bar': {
            borderRadius: 999,
            background: `linear-gradient(90deg, ${theme.palette.success.main}, ${theme.palette.secondary.main})`,
          },
        }}
      />
    </Box>
  );
}

export default function EmployeeDashboard() {
  const theme = useTheme();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null);
  const [error, setError] = useState('');
  const [employee, setEmployee] = useState(null);
  const [contractType, setContractType] = useState('Unknown');
  const [vacations, setVacations] = useState([]);
  const [requests, setRequests] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [projects, setProjects] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [lat, setLat] = useState(null);
  const [lng, setLng] = useState(null);
  const [geoStatus, setGeoStatus] = useState('');
  const [attendanceMsg, setAttendanceMsg] = useState(null);
  const [isCheckedIn, setIsCheckedIn] = useState(false);

  const watchId = useRef(null);
  const role = localStorage.getItem('role') || 'employee';
  const vacationRequestsRoute =
    role === 'finance_coordinator' ? '/finance_coordinator/vacation-requests' : '/employee/vacation-requests';

  useEffect(() => {
    fetchAll();
    startLiveLocation();
    return () => {
      if (watchId.current) navigator.geolocation.clearWatch(watchId.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (employee) checkIfCheckedIn();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employee]);

  async function fetchAll(options = {}) {
    const silent = Boolean(options?.silent);
    if (silent) setRefreshing(true);
    else setLoading(true);
    setError('');

    try {
      const isFinanceCoordinator = role === 'finance_coordinator';
      const dashboardUrl = isFinanceCoordinator ? '/finance_coordinator/dashboard' : '/employee/dashboard';
      const requestsUrl = isFinanceCoordinator ? '/finance_coordinator/vacation-requests' : '/employee/vacation-requests';

      const dash = await api.get(dashboardUrl);
      const dashEmployee = dash.data?.employee || null;
      setEmployee(dashEmployee);
      setVacations(asArray(dash.data?.vacations));
      setContractType(dashEmployee?.contract_type || 'Unknown');
      setAnalytics(dash.data?.analytics || null);

      const [requestResult, holidayResult, announcementResult, projectResult] = await Promise.allSettled([
        api.get(requestsUrl),
        api.get('/holidays?future=1&active=1'),
        api.get('/announcements'),
        api.get('/projects'),
      ]);

      setRequests(requestResult.status === 'fulfilled' ? asArray(requestResult.value.data) : []);
      setHolidays(holidayResult.status === 'fulfilled' ? asArray(holidayResult.value.data) : []);
      setAnnouncements(announcementResult.status === 'fulfilled' ? asArray(announcementResult.value.data) : []);
      setProjects(projectResult.status === 'fulfilled' ? asArray(projectResult.value.data) : []);
      setLastUpdatedAt(new Date());
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to load dashboard data.');
    } finally {
      if (silent) setRefreshing(false);
      else setLoading(false);
    }
  }

  function startLiveLocation() {
    if (!navigator.geolocation) {
      setGeoStatus('Geolocation is not supported in this browser.');
      return;
    }

    setGeoStatus('Acquiring location...');
    watchId.current = navigator.geolocation.watchPosition(
      (pos) => {
        setLat(pos.coords.latitude);
        setLng(pos.coords.longitude);
        setGeoStatus('Location updated');
      },
      (err) => setGeoStatus(err.message),
      { enableHighAccuracy: true, timeout: 20000 }
    );
  }

  async function checkIfCheckedIn() {
    try {
      const today = new Date();
      const yyyy = today.getFullYear();
      const mm = String(today.getMonth() + 1).padStart(2, '0');
      const dd = String(today.getDate()).padStart(2, '0');
      const params = {
        employee_code: employee.employee_code,
        type: 'CheckIn',
        from: `${yyyy}-${mm}-${dd} 00:00:00`,
        to: `${yyyy}-${mm}-${dd} 23:59:59`,
      };
      const res = await api.get('/attendances', { params });
      setIsCheckedIn(asArray(res.data).length > 0);
    } catch (err) {
      setAttendanceMsg(err?.response?.data?.message || 'Unable to verify today attendance status.');
    }
  }

  async function handleCheck(type) {
    if (!employee?.employee_code) {
      setAttendanceMsg('Employee code is missing.');
      return;
    }
    if (!nearbyProjectId) {
      setAttendanceMsg('Not inside any project radius.');
      return;
    }

    try {
      const res = await api.post('/attendances', {
        employee_code: employee.employee_code,
        project_id: nearbyProjectId,
        device_stgid: 'WEB-01',
        log_time: toMysql(new Date()),
        type,
        input_type: 'GPS',
        raw_payload: JSON.stringify({ latitude: lat, longitude: lng }),
      });
      setAttendanceMsg(`${type} successful. Ref #${res.data.id}`);
      setIsCheckedIn(type === 'CheckIn');
    } catch (err) {
      setAttendanceMsg(err.response?.data?.message || 'Failed to record attendance.');
    }
  }

  const balances = useMemo(
    () =>
      asArray(vacations).map((balance) => {
        const current = Number(balance.current_balance || 0);
        return {
          ...balance,
          current_balance: current,
          projected_balance: current + getDailyAccrualRate(contractType, balance.leave_type) * daysRemainingInYear(),
        };
      }),
    [contractType, vacations]
  );

  const totalBalance = useMemo(
    () => balances.reduce((sum, balance) => sum + Number(balance.current_balance || 0), 0),
    [balances]
  );

  const recentRequests = useMemo(
    () =>
      asArray(requests)
        .slice()
        .sort((a, b) => new Date(requestDate(b)) - new Date(requestDate(a)))
        .slice(0, 5),
    [requests]
  );

  const upcomingHolidays = useMemo(
    () =>
      asArray(holidays)
        .filter((holiday) => isHolidayUpcoming(holiday))
        .slice()
        .sort((a, b) => new Date(a.holiday_date) - new Date(b.holiday_date))
        .slice(0, 4),
    [holidays]
  );

  const activeAnnouncements = useMemo(
    () => asArray(announcements).filter((announcement) => isAnnouncementActive(announcement)).slice(0, 4),
    [announcements]
  );

  const nextHoliday = upcomingHolidays[0] || null;
  const nextHolidayCountdown = nextHoliday ? daysUntilDate(nextHoliday.holiday_date) : null;
  const pendingRequests = recentRequests.filter((request) => normalizeStatus(request.status) === 'pending').length;
  const kpis = analytics?.kpis || {};
  const nearbyProjectId = lat !== null && lng !== null ? getNearbyProjectId(lat, lng, projects) : null;
  const nearbyProject = nearbyProjectId ? projects.find((project) => project.id === nearbyProjectId) : null;
  const canRecordAttendance = Boolean(nearbyProjectId && employee?.employee_code);
  const employeeName = employee?.name || 'there';
  const roleLabel = role === 'finance_coordinator' ? 'Finance Coordinator' : 'Employee';

  if (loading) {
    return (
      <PageLayout backgroundAccent maxWidth="xl">
        <LoadingState rows={10} />
      </PageLayout>
    );
  }

  return (
    <PageLayout backgroundAccent maxWidth="xl">
      {error ? (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      ) : null}

      <Stack spacing={1.55}>
        <Box
          sx={{
            position: 'relative',
            overflow: 'hidden',
            borderRadius: { xs: '16px', md: '14px' },
            p: { xs: 1.55, sm: 2, md: 2.15 },
            color: '#102033',
            border: `1px solid ${alpha('#9db2c8', 0.38)}`,
            background:
              `linear-gradient(180deg, ${alpha('#ffffff', 0.98)} 0%, ${alpha('#f7fbff', 0.98)} 100%)`,
            boxShadow: '0 16px 38px rgba(15, 23, 42, 0.08)',
            backdropFilter: 'blur(12px)',
          }}
        >
          <Box
            aria-hidden
            sx={{
              position: 'absolute',
              inset: 0,
              background:
                `linear-gradient(115deg, transparent 0 42%, ${alpha(theme.palette.secondary.main, 0.07)} 42% 43%, transparent 43% 100%),` +
                `radial-gradient(760px 260px at 12% 0%, ${alpha(theme.palette.secondary.main, 0.1)}, transparent 62%),` +
                `radial-gradient(540px 220px at 100% 12%, ${alpha(theme.palette.success.main, 0.1)}, transparent 64%)`,
              pointerEvents: 'none',
            }}
          />

          <Box
            aria-hidden
            sx={{
              position: 'absolute',
              right: { xs: -155, md: 250 },
              top: { xs: 70, md: 8 },
              width: { xs: 360, md: 560 },
              height: 190,
              opacity: { xs: 0.2, md: 0.36 },
              pointerEvents: 'none',
            }}
          >
            <svg viewBox="0 0 520 250" width="100%" height="100%" role="presentation" focusable="false">
              <path
                d="M24 174 C96 58, 167 80, 226 137 S356 226, 488 64"
                fill="none"
                stroke={alpha(theme.palette.secondary.main, 0.7)}
                strokeWidth="7"
                strokeLinecap="round"
                strokeDasharray="1 18"
              />
              {[24, 166, 316, 488].map((cx, idx) => (
                <g key={cx}>
                  <circle cx={cx} cy={idx === 0 ? 174 : idx === 1 ? 88 : idx === 2 ? 190 : 64} r="21" fill={alpha('#ffffff', 0.82)} />
                  <circle cx={cx} cy={idx === 0 ? 174 : idx === 1 ? 88 : idx === 2 ? 190 : 64} r="10" fill={idx === 2 ? theme.palette.success.main : theme.palette.secondary.main} />
                </g>
              ))}
            </svg>
          </Box>

          <Box
            sx={{
              position: 'relative',
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 0.88fr) minmax(520px, 1.12fr)' },
              gap: { xs: 1.55, md: 2.2 },
              alignItems: 'center',
            }}
          >
            <Box>
              <Stack spacing={1.35}>
                <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
                  <Chip
                    size="small"
                    label={roleLabel}
                    sx={{
                      color: '#0f3b57',
                      backgroundColor: alpha(theme.palette.secondary.main, 0.1),
                      border: `1px solid ${alpha(theme.palette.secondary.main, 0.2)}`,
                      fontWeight: 900,
                    }}
                  />
                  <Chip
                    size="small"
                    label={isCheckedIn ? 'Checked in today' : 'Not checked in yet'}
                    sx={{
                      color: isCheckedIn ? '#166534' : '#92400e',
                      backgroundColor: alpha(isCheckedIn ? theme.palette.success.main : theme.palette.warning.main, 0.13),
                      border: `1px solid ${alpha(isCheckedIn ? theme.palette.success.main : theme.palette.warning.main, 0.26)}`,
                      fontWeight: 900,
                    }}
                  />
                </Stack>
                <Box>
                  <Typography variant="h3" sx={{ color: '#102653', fontWeight: 950, maxWidth: 820, lineHeight: 1.02, letterSpacing: 0 }}>
                    Welcome back, {employeeName}
                  </Typography>
                  <Typography variant="body1" sx={{ color: '#52657a', fontWeight: 650, mt: 0.85, maxWidth: 760, lineHeight: 1.35 }}>
                    Today&apos;s work, requests, and leave status.
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ color: '#52657a', maxWidth: 720, lineHeight: 1.6 }}>
                  {[
                    employee?.position,
                    employee?.department,
                    `Service ${calculateServicePeriod(employee?.joining_date)}`,
                    contractType !== 'Unknown' ? `Contract ${contractType}` : null,
                  ].filter(Boolean).join(' / ')}
                </Typography>
                <Stack direction="row" spacing={1.3} alignItems="center" useFlexGap flexWrap="wrap" sx={{ pt: 1.2 }}>
                  <Chip
                    icon={<CheckCircleIcon />}
                    label={isCheckedIn ? 'Checked in today' : 'Not checked in yet'}
                    sx={{
                      height: 38,
                      px: 0.85,
                      color: isCheckedIn ? '#16824c' : '#a16207',
                      backgroundColor: alpha(isCheckedIn ? theme.palette.success.main : theme.palette.warning.main, 0.1),
                      border: `1px solid ${alpha(isCheckedIn ? theme.palette.success.main : theme.palette.warning.main, 0.24)}`,
                      fontWeight: 900,
                      '& .MuiChip-icon': {
                        color: isCheckedIn ? '#16824c' : '#a16207',
                      },
                    }}
                  />
                  <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700 }}>
                    {isCheckedIn ? 'Check out when your day is done.' : 'Check in when you are inside project radius.'}
                  </Typography>
                </Stack>
              </Stack>
            </Box>

            <Box>
              <Stack spacing={1.15} alignItems={{ xs: 'stretch', lg: 'flex-end' }}>
                <Stack direction="row" spacing={1} justifyContent={{ xs: 'flex-start', lg: 'flex-end' }} useFlexGap flexWrap="wrap">
                  <Button
                    variant="outlined"
                    startIcon={<RefreshIcon />}
                    onClick={() => fetchAll({ silent: true })}
                    disabled={refreshing}
                    sx={{
                      minWidth: 142,
                      borderRadius: '10px',
                      color: '#102653',
                      borderColor: alpha('#1d4f8f', 0.28),
                      backgroundColor: alpha('#ffffff', 0.72),
                      fontWeight: 900,
                      '&:hover': {
                        borderColor: alpha('#1d4f8f', 0.46),
                        backgroundColor: '#ffffff',
                      },
                    }}
                  >
                    {refreshing ? 'Refreshing...' : 'Refresh'}
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => navigate('/requests/new')}
                    sx={{
                      minWidth: 160,
                      borderRadius: '10px',
                      fontWeight: 900,
                      background: 'linear-gradient(90deg, #0072ce, #0697b8)',
                      boxShadow: '0 12px 24px rgba(14, 116, 144, 0.24)',
                      '&:hover': { background: 'linear-gradient(90deg, #075aa0, #0787a5)' },
                    }}
                  >
                    New Request
                  </Button>
                </Stack>

                <Box
                  sx={{
                    width: '100%',
                    p: { xs: 1, md: 1.2 },
                    borderRadius: '12px',
                    backgroundColor: alpha('#ffffff', 0.92),
                    border: '1px solid',
                    borderColor: alpha('#9db2c8', 0.35),
                    boxShadow: '0 18px 36px rgba(15,23,42,0.1)',
                  }}
                >
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', sm: '1fr auto 1fr' },
                      gap: { xs: 1.1, sm: 1.4 },
                      alignItems: 'center',
                    }}
                  >
                    <Stack direction="row" spacing={1.2} alignItems="center">
                      <Box
                        sx={{
                          width: 64,
                          height: 64,
                          borderRadius: '50%',
                          display: 'grid',
                          placeItems: 'center',
                          color: '#ffffff',
                          background: `linear-gradient(145deg, ${theme.palette.success.main}, ${theme.palette.success.dark})`,
                          border: `7px solid ${alpha(theme.palette.success.main, 0.16)}`,
                          flexShrink: 0,
                        }}
                      >
                        <CheckCircleIcon sx={{ fontSize: 32 }} />
                      </Box>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 850 }}>
                          Attendance
                        </Typography>
                        <Typography variant="subtitle2" sx={{ color: '#102653', fontWeight: 900 }}>
                          {isCheckedIn ? 'Checked in' : 'Ready to check in'}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#52657a' }}>
                          {isCheckedIn ? 'Today' : canRecordAttendance ? 'Inside project radius' : 'Waiting for project radius'}
                        </Typography>
                      </Box>
                    </Stack>

                    <Box sx={{ display: { xs: 'none', sm: 'block' }, width: '1px', alignSelf: 'stretch', backgroundColor: alpha('#9db2c8', 0.38) }} />

                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
                      <Button
                        variant="contained"
                        startIcon={isCheckedIn ? <LogoutIcon /> : <LoginIcon />}
                        disabled={!canRecordAttendance}
                        onClick={() => handleCheck(isCheckedIn ? 'CheckOut' : 'CheckIn')}
                        sx={{
                          borderRadius: '10px',
                          minWidth: 132,
                          fontWeight: 900,
                          background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                          '&.Mui-disabled': {
                            color: '#64748b',
                            background: alpha(theme.palette.primary.main, 0.1),
                          },
                        }}
                      >
                        {isCheckedIn ? 'Check Out' : 'Check In'}
                      </Button>
                      <Button
                        variant="outlined"
                        startIcon={<RequestPageRoundedIcon />}
                        onClick={() => navigate('/requests/new')}
                        sx={{
                          borderRadius: '10px',
                          minWidth: 162,
                          color: '#102653',
                          borderColor: alpha('#1d4f8f', 0.3),
                          fontWeight: 900,
                          backgroundColor: alpha('#ffffff', 0.7),
                        }}
                      >
                        Open Request Center
                      </Button>
                    </Stack>
                  </Box>

                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                      gap: 1,
                      mt: 1,
                    }}
                  >
                    {[
                      {
                        icon: <LocationSearchingIcon fontSize="small" />,
                        label: 'Current Location',
                        value: nearbyProject ? 'Inside project radius' : geoStatus || 'Waiting for browser location',
                        sub: nearbyProject?.name || '',
                      },
                      {
                        icon: <CalendarMonthIcon fontSize="small" />,
                        label: 'Next Holiday',
                        value: nextHolidayCountdown != null ? `${nextHolidayCountdown} days remaining` : 'No upcoming holiday',
                        sub: nextHoliday ? `${nextHoliday.name || 'Holiday'} - ${formatDateOnly(nextHoliday.holiday_date)}` : '',
                      },
                    ].map((item) => (
                      <Stack key={item.label} direction="row" spacing={1} alignItems="flex-start">
                        <Box sx={{ color: theme.palette.primary.main, display: 'grid', placeItems: 'center', pt: 0.25 }}>
                          {item.icon}
                        </Box>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 850 }}>
                            {item.label}
                          </Typography>
                          <Typography variant="body2" sx={{ color: item.value.includes('Inside') ? '#16824c' : '#102033', fontWeight: 900 }}>
                            {item.value}
                          </Typography>
                          {item.sub ? (
                            <Typography variant="caption" sx={{ color: '#52657a', display: 'block' }}>
                              {item.sub}
                            </Typography>
                          ) : null}
                        </Box>
                      </Stack>
                    ))}
                  </Box>
                </Box>
              </Stack>
            </Box>
          </Box>
        </Box>

        {attendanceMsg ? (
          <Alert severity={attendanceMsg.toLowerCase().includes('successful') ? 'success' : 'info'} onClose={() => setAttendanceMsg(null)}>
            {attendanceMsg}
          </Alert>
        ) : null}

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', md: 'repeat(4, minmax(0, 1fr))' },
            gap: 1.1,
          }}
        >
          <Box>
            <SummaryStat
              icon={<CheckCircleIcon fontSize="small" />}
              label="Leave Available"
              value={totalBalance.toFixed(1)}
              hint={`${balances.length} balance ${balances.length === 1 ? 'type' : 'types'}`}
              color="success.main"
            />
          </Box>
          <Box>
            <SummaryStat
              icon={<HourglassEmptyIcon fontSize="small" />}
              label="Pending Requests"
              value={kpis.pending_requests ?? pendingRequests}
              hint="Waiting for action"
              color="warning.main"
            />
          </Box>
          <Box>
            <SummaryStat
              icon={<TodayRoundedIcon fontSize="small" />}
              label="Present Days"
              value={kpis.present_days_mtd ?? '-'}
              hint="Month to date"
              color="primary.main"
            />
          </Box>
          <Box>
            <SummaryStat
              icon={<CalendarMonthIcon fontSize="small" />}
              label="Next Holiday"
              value={nextHolidayCountdown != null ? `${nextHolidayCountdown}d` : '-'}
              hint={nextHoliday?.name || 'No upcoming holiday'}
              color="secondary.main"
            />
          </Box>
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1.55fr) minmax(330px, 0.45fr)' },
            gap: 1.4,
            alignItems: 'start',
          }}
        >
          <Box>
            <Stack spacing={1.4}>
              <DashboardPanel
                title="Quick Actions"
                subtitle="Fast links for common employee work."
              >
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
                    gap: 1,
                  }}
                >
                  <Box>
                    <DashboardAction
                      title="New Request"
                      description="Open vacation, exit/entry, or HR request forms."
                      icon={<AddIcon fontSize="small" />}
                      badge="Start"
                      onClick={() => navigate('/requests/new')}
                    />
                  </Box>
                  <Box>
                    <DashboardAction
                      title="My Requests"
                      description="Review status, attachments, and request history."
                      icon={<RequestPageRoundedIcon fontSize="small" />}
                      color="secondary.main"
                      onClick={() => navigate(vacationRequestsRoute)}
                    />
                  </Box>
                  <Box>
                    <DashboardAction
                      title="Vacation Form"
                      description="Submit leave with travel and contact details."
                      icon={<FlightTakeoffRoundedIcon fontSize="small" />}
                      color="success.main"
                      onClick={() => navigate('/vacation-request')}
                    />
                  </Box>
                  <Box>
                    <DashboardAction
                      title="Profile"
                      description="Check personal, bank, asset, and certificate details."
                      icon={<PersonIcon fontSize="small" />}
                      color="info.main"
                      onClick={() => navigate('/profile')}
                    />
                  </Box>
                </Box>
              </DashboardPanel>

              <DashboardPanel
                title="Recent Requests"
                subtitle="Latest vacation requests from your employee queue."
                action={<Button size="small" onClick={() => navigate(vacationRequestsRoute)}>View all</Button>}
              >
                {recentRequests.length ? (
                  <Stack spacing={1}>
                    {recentRequests.map((request) => (
                      <Box
                        key={request.id || `${request.leave_type}-${requestDate(request)}`}
                        sx={{
                          p: 1.3,
                          borderRadius: '12px',
                          border: '1px solid',
                          borderColor: theme.hr?.line?.soft || 'divider',
                          backgroundColor: theme.palette.mode === 'dark' ? alpha('#ffffff', 0.035) : alpha(theme.palette.primary.main, 0.025),
                        }}
                      >
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between">
                          <Box>
                            <Typography variant="subtitle2" sx={{ fontWeight: 850 }}>
                              {request.leave_type || request.request_type || 'Request'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {[formatDateOnly(request.start_date), formatDateOnly(request.end_date)].filter(Boolean).join(' - ') || formatDateOnly(requestDate(request))}
                            </Typography>
                          </Box>
                          <StatusBadge status={normalizeStatus(request.status)} />
                        </Stack>
                      </Box>
                    ))}
                  </Stack>
                ) : (
                  <MiniEmpty title="No recent requests" description="Your latest requests will appear here." />
                )}
              </DashboardPanel>
            </Stack>
          </Box>

          <Box>
            <Stack spacing={1.4}>
              <DashboardPanel title="Leave Balances" subtitle="Current balance and projected annual accrual.">
                {balances.length ? (
                  <Stack
                    spacing={0.8}
                    sx={{
                      maxHeight: { lg: 260 },
                      overflowY: 'auto',
                      pr: balances.length > 4 ? 0.4 : 0,
                      '&::-webkit-scrollbar': { width: 6 },
                      '&::-webkit-scrollbar-thumb': {
                        borderRadius: 999,
                        backgroundColor: alpha(theme.palette.primary.main, 0.22),
                      },
                    }}
                  >
                    {balances.map((balance) => (
                      <LeaveBalanceRow key={balance.id || balance.leave_type} balance={balance} contractType={contractType} />
                    ))}
                  </Stack>
                ) : (
                  <MiniEmpty title="No leave balances" />
                )}
              </DashboardPanel>

              <DashboardPanel title="Upcoming Holidays" subtitle="Next holidays relevant to your workspace.">
                {upcomingHolidays.length ? (
                  <Stack spacing={1}>
                    {upcomingHolidays.map((holiday) => (
                      <DetailField
                        key={holiday.id || holiday.holiday_date}
                        label={formatDateOnly(holiday.holiday_date)}
                        value={holiday.name || 'Holiday'}
                        icon={<CalendarMonthIcon fontSize="small" />}
                      />
                    ))}
                  </Stack>
                ) : (
                  <MiniEmpty title="No upcoming holidays" description="Future holidays will show here." />
                )}
              </DashboardPanel>

              <DashboardPanel title="Announcements" subtitle="Active messages from HR and the organization.">
                {activeAnnouncements.length ? (
                  <Stack spacing={1}>
                    {activeAnnouncements.map((announcement) => (
                      <DetailField
                        key={announcement.id || announcement.title}
                        label={announcement.title || 'Announcement'}
                        value={announcement.message || announcement.description || '-'}
                        icon={<CampaignIcon fontSize="small" />}
                      />
                    ))}
                  </Stack>
                ) : (
                  <MiniEmpty title="No active announcements" description="New HR messages will show here." />
                )}
              </DashboardPanel>
            </Stack>
          </Box>
        </Box>

        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
          <Chip icon={<ApartmentIcon />} label={employee?.department || 'No department'} />
          <Chip label={contractType !== 'Unknown' ? contractType : 'No contract type'} />
          {lastUpdatedAt ? <Chip label={`Updated ${lastUpdatedAt.toLocaleTimeString()}`} /> : null}
        </Stack>
      </Stack>
    </PageLayout>
  );
}
